(() => {
  // app/scripts/coveo-search-analytics.js
  var ANALYTICS_EVENT_TYPE = "search";
  var CoveoSearchAnalytics = class {
    constructor(engine) {
      this.engine = engine;
    }
    get _config() {
      const { organizationId, accessToken, analytics } = this.engine.configuration;
      return {
        organizationId,
        accessToken,
        trackingId: analytics.trackingId
      };
    }
    get _endpoint() {
      const { organizationId } = this._config;
      return `https://${organizationId}.analytics.org.coveo.com/rest/ua/v15/analytics/${ANALYTICS_EVENT_TYPE}`;
    }
    get _searchState() {
      return this.engine.store.getState().commerceSearch || {};
    }
    _getClientId() {
      try {
        return this.engine.relay.getMeta("").clientId;
      } catch (error) {
        console.warn(`[CoveoSearchAnalytics] Failed to get clientId:`, error);
        return void 0;
      }
    }
    _getFacetState(pendingChange = null) {
      const { facets } = this._searchState;
      if (!Array.isArray(facets))
        return [];
      return facets.flatMap(
        (facet) => facet.values.reduce((acc, v, index) => {
          let isSelected = v.state === "selected";
          if (pendingChange && pendingChange.facetId === facet.facetId && pendingChange.value === v.value) {
            isSelected = pendingChange.action === "select";
          }
          if (isSelected) {
            acc.push({
              field: facet.facetId,
              id: facet.facetId,
              title: facet.displayName || facet.facetId,
              facetType: "specific",
              value: v.value,
              valuePosition: index + 1,
              displayValue: v.value,
              state: "selected"
            });
          }
          return acc;
        }, [])
      );
    }
    _buildPayload(actionCause, customData = {}, { pendingChange = null, responseId: responseIdOverride } = {}) {
      const { trackingId } = this._config;
      const state = this.engine.store.getState();
      const { responseId, queryExecuted } = state.commerceSearch || {};
      const language = state.commerceContext?.language || document.documentElement.lang || "en";
      return {
        actionCause,
        searchQueryUid: responseIdOverride || responseId,
        queryText: queryExecuted || "",
        language,
        originLevel1: trackingId,
        originLevel2: "default",
        originLevel3: window.location.href,
        clientId: this._getClientId(),
        anonymous: false,
        responseTime: 0,
        customData,
        facetState: this._getFacetState(pendingChange ?? null)
      };
    }
    async logSearchEvent(actionCause, customData = {}, options = {}) {
      const { accessToken } = this._config;
      if (!accessToken)
        return;
      const payload = this._buildPayload(actionCause, customData, options);
      try {
        const response = await fetch(this._endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          console.warn(`[CoveoSearchAnalytics] ${actionCause} failed:`, response.status);
        }
      } catch (error) {
        console.warn(`[CoveoSearchAnalytics] ${actionCause} error:`, error);
      }
    }
    logInterfaceLoad() {
      return this.logSearchEvent("interfaceLoad");
    }
    logSearchboxSubmit() {
      return this.logSearchEvent("searchboxSubmit");
    }
    logSearchFromLink() {
      return this.logSearchEvent("searchFromLink");
    }
    logOmniboxAnalytics(metadata = {}) {
      return this.logSearchEvent("omniboxAnalytics", metadata);
    }
    logRecommendationInterfaceLoad(responseId) {
      return this.logSearchEvent("recommendationInterfaceLoad", {}, { responseId });
    }
    logFacetSelect(facetId, facetValue) {
      return this.logSearchEvent("facetSelect", {
        facetId,
        facetValue,
        facetField: facetId
      }, { pendingChange: { facetId, value: facetValue, action: "select" } });
    }
    logFacetDeselect(facetId, facetValue) {
      return this.logSearchEvent("facetDeselect", {
        facetId,
        facetValue,
        facetField: facetId
      }, { pendingChange: { facetId, value: facetValue, action: "deselect" } });
    }
    logFacetClearAll() {
      return this.logSearchEvent("facetClearAll");
    }
  };
  var coveo_search_analytics_default = CoveoSearchAnalytics;

  // app/scripts/related-products.js
  var { buildRecommendations } = coveoheadless;
  var { delay } = theme;
  var RelatedProducts = class extends HTMLElement {
    constructor() {
      super();
      this.sliderContainer = this.querySelector(".js-slider");
      this.slotId = this.getAttribute("data-slot-id");
      this.variantId = this.getAttribute("data-variant-id");
      this.limit = this.getAttribute("data-limit");
    }
    async connectedCallback() {
      const searchToken = await CoveoUtils.getSearchToken();
      if (!searchToken) {
        console.error("Failed to fetch search token");
        return;
      }
      const engine = await window.configureHeadless(searchToken);
      this.analytics = new coveo_search_analytics_default(engine);
      this.recommendationsController = buildRecommendations(engine, {
        options: {
          slotId: this.slotId,
          productId: `gid://shopify/ProductVariant/${this.variantId}`
        }
      });
      const pagination = this.recommendationsController.pagination();
      this.recommendationsController.subscribe(this.renderProducts.bind(this));
      pagination.setPageSize(+this.limit);
    }
    async renderProducts() {
      try {
        const { products } = this.recommendationsController.state;
        if (!products.length) {
          return;
        }
        const cardProducts = await Promise.all(
          products.map(async (product) => {
            if (!product.clickUri && !product) {
              return false;
            }
            const handle = product.clickUri.split("/").pop();
            let linkToFetch;
            if (location.pathname.includes("/collections") && theme.routes.currentCollectionUrl) {
              linkToFetch = `${theme.routes.currentCollectionUrl}/products/${handle}`;
            } else {
              linkToFetch = `${Shopify.routes.root}products/${handle}`;
            }
            const html = await fetch(`${linkToFetch}?section_id=card-product`).then((res) => res.text());
            const div = document.createElement("div");
            div.innerHTML = html;
            const productCard = div.firstElementChild;
            const productLinks = productCard.querySelectorAll("a[data-product-id]");
            const params = new URLSearchParams(window.location.search);
            const parentCollectionHandle = params.get("pc");
            productLinks.forEach((link) => {
              link.addEventListener("click", async (e) => {
                const productId = link.dataset.productId;
                const url = new URL(link.href);
                if (parentCollectionHandle) {
                  url.searchParams.set("pc", parentCollectionHandle);
                }
                if (productId) {
                  e.preventDefault();
                  this.recommendationsController.interactiveProduct({ options: { product } }).select();
                  await delay(100);
                }
                location.href = url.toString();
              });
            });
            productCard.classList.add("swiper-slide", "!h-auto");
            return productCard;
          })
        );
        this.sliderContainer.append(...cardProducts);
        this.classList.remove("hidden");
        this.analytics?.logRecommendationInterfaceLoad(this.recommendationsController.state.responseId);
        window.PriceInventory.fetchLivePriceInventory();
      } catch (error) {
        throw error;
      }
    }
  };
  customElements.define("related-products", RelatedProducts);
})();
